import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DataService } from 'src/app/services/data.service';
import { SiteService } from 'src/app/services/site.service';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { DripProgram, DripProgramDetails, Campaign, EmailType } from 'src/app/models/communication/campaign';
import { Employer } from 'src/app/models/employer';
import { forkJoin, Observable } from 'rxjs';
import { ConfirmationService } from 'primeng/api';
import { IPendingChangesComponent } from 'src/app/guards/pending-changes.guard';

interface DripProgramEmail {
	campaignID: string;
	campaignName: string;
	sequence: number;
	interval: number;
	catchup: boolean;
}

interface DripProgramModel {
	startDate: Date | null;
	deliveryTime: Date | null;
	pause: boolean;
	daysBetweenCatchup: number;
}

@Component({
  selector: 'app-dripprogram',
  templateUrl: './dripprogram.component.html',
  styleUrls: ['./dripprogram.component.scss']
})
export class DripprogramComponent implements OnInit, IPendingChangesComponent {

	public dripProgram: DripProgram = new DripProgram();
	public dripProgramID: string = '';
	public defaultDripProgramID: string = '';
	public dripPrograms: DripProgram[] = [];
	public selectedDripProgramID: string = '';
	public emails: DripProgramEmail[] = [];
	public availableCampaigns: Campaign[] = [];
	public loading: boolean = false;
	public deliveryTimeDate: Date | null = null; // Separate property for UI binding
	public showCampaignDialog: boolean = false;
	public filteredAvailableCampaigns: Campaign[] = [];
	public campaignSearchTerm: string = "";
	public isDirty: boolean = false;
	public dirtySwitchDialogVisible: boolean = false;
	public missingEmployers: any[] = [];
	public showMissingWarning: boolean = false;
	public dirtyDialogMessage: string = '';
	// Runs after the user saves or discards in the unsaved-changes dialog (switch program, copy program).
	private pendingAction: (() => void) | null = null;

	// Copy Program dialog
	public showCopyDialog: boolean = false;
	public copyProgramName: string = '';
	public copyEmployerID: string | null = null;
	public copyEmployers: Employer[] = [];
	public copyInFlight: boolean = false;

	// Deprecated static options replaced by API-provided list

	constructor(private dataService: DataService, private siteService: SiteService, private router: Router, private route: ActivatedRoute, private confirmationService: ConfirmationService) { }

	ngOnInit() {
		this.siteService.pageTitle = 'Drip Programs';
		
		this.updateHeaderButtons();

		this.loadInitial();
	}

	private updateHeaderButtons() {
		this.siteService.headerButtons = [
			{ text: 'Save', class: 'success', callback: () => this.save() }
		];
	}

	loadInitial() {
		this.loading = true;
		// First get default ID and list of programs
		forkJoin({
			config: this.dataService.getDefaultDripProgramConfig(),
			list: this.dataService.getDripPrograms(),
		}).subscribe({
			next: (config) => {
				this.defaultDripProgramID = config.config.defaultDripProgramID;
				this.dripPrograms = config.list;
				// ?programID= wins when it names an existing program; otherwise fall back to the default
				const requestedID = this.route.snapshot.queryParamMap.get('programID');
				const requested = requestedID ? this.findProgram(requestedID) : undefined;
				if (requestedID && !requested) this.syncProgramQueryParam(null);
				this.selectedDripProgramID = requested?.dripProgramID ?? this.defaultDripProgramID;
				this.dripProgramID = this.selectedDripProgramID;
				// Update header buttons now that we have default ID
				this.updateHeaderButtons();
				// Load current selection
				this.loadProgramAndDetails(this.dripProgramID);
			},
			error: (error) => {
				console.error('Error loading drip program configuration/list:', error);
				this.siteService.setMessage('Error loading drip programs', 'error');
				this.loading = false;
			}
		});
	}

	private loadProgramAndDetails(programID: string) {
		this.loading = true;
		forkJoin({
			dripProgram: this.dataService.getDripProgram(programID),
			dripProgramDetails: this.dataService.getDripProgramDetails(programID),
			campaigns: this.dataService.getCampaigns(undefined, undefined, EmailType.Drip)
		}).subscribe({
			next: (data) => {
				this.dripProgramID = programID;
				this.dripProgram = new DripProgram(data.dripProgram);
				// Ensure dripProgramDesc is populated for UI binding
				if (!this.dripProgram.dripProgramDesc && this.dripProgram.dripProgramName) {
					this.dripProgram.dripProgramDesc = this.dripProgram.dripProgramName;
				}
				this.availableCampaigns = data.campaigns;
				this.mapDetailsToEmails(data.dripProgramDetails);
				this.loading = false;
				this.isDirty = false;
			},
			error: (error) => {
				console.error('Error loading drip program details:', error);
				this.siteService.setMessage('Error loading drip program', 'error');
				this.loading = false;
			}
		});
	}

	get isDefaultProgram(): boolean {
		return !!this.selectedDripProgramID && this.sameID(this.selectedDripProgramID, this.defaultDripProgramID);
	}

	private sameID(a: string | null | undefined, b: string | null | undefined): boolean {
		return (a ?? '').toLowerCase() === (b ?? '').toLowerCase();
	}

	private findProgram(programID: string): DripProgram | undefined {
		return this.dripPrograms.find(p => this.sameID(p.dripProgramID, programID));
	}

	// Keeps ?programID= in the URL matching the selection so a refresh reopens the same program.
	private syncProgramQueryParam(programID: string | null) {
		this.router.navigate([], {
			relativeTo: this.route,
			queryParams: { programID: programID || null },
			queryParamsHandling: 'merge',
			replaceUrl: true
		});
	}

	private selectProgram(programID: string) {
		this.selectedDripProgramID = programID;
		this.updateHeaderButtons();
		this.syncProgramQueryParam(programID);
		this.loadProgramAndDetails(programID);
	}

	onProgramSelectionChange(nextID: string) {
		if (!nextID || nextID === this.dripProgramID) return;
		if (this.isDirty) {
			this.confirmUnsavedChanges('Do you want to save before switching programs?', () => this.selectProgram(nextID));
			return;
		}
		this.selectProgram(nextID);
	}

	mapDetailsToEmails(details: DripProgramDetails[]) {
		this.emails = details
			.sort((a, b) => a.sequence - b.sequence)
			.map(detail => {
				const campaign = this.availableCampaigns.find(c => c.campaignID === detail.campaignID);
				return {
					campaignID: detail.campaignID,
					campaignName: campaign ? campaign.campaignName : '*** Campaign no longer configured for Drip ***',
					sequence: detail.sequence,
					interval: detail.interval,
					catchup: detail.catchup
				};
			});
		
		// Convert start date string to Date object for UI binding
		if (this.dripProgram.startDate && typeof this.dripProgram.startDate === 'string') {
			this.dripProgram.startDate = new Date(this.dripProgram.startDate);
		}
		
		// Convert delivery time string to Date for UI binding
		if (this.dripProgram.deliveryTime && typeof this.dripProgram.deliveryTime === 'string') {
			const [hours, minutes] = this.dripProgram.deliveryTime.split(':');
			this.deliveryTimeDate = new Date();
			this.deliveryTimeDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
		}
	}

	save() {
		return new Promise<boolean>((resolve, reject) => {
			if (!this.dripProgram || !this.dripProgramID) {
				this.siteService.setMessage('No drip program to save', 'error');
				reject(false);
				return;
			}

			this.loading = true;

			// Convert time back to string format for API
			if (this.deliveryTimeDate) {
				const hours = this.deliveryTimeDate.getHours().toString().padStart(2, '0');
				const minutes = this.deliveryTimeDate.getMinutes().toString().padStart(2, '0');
				this.dripProgram.deliveryTime = `${hours}:${minutes}:00`;
			}

			// Ensure startDate is a proper Date object for JSON serialization
			if (this.dripProgram.startDate && typeof this.dripProgram.startDate === 'string') {
				this.dripProgram.startDate = new Date(this.dripProgram.startDate);
			}

			// Create details array from emails
			const details: DripProgramDetails[] = this.emails.map((email, index) => new DripProgramDetails({
				dripProgramID: this.dripProgramID,
				campaignID: email.campaignID,
				sequence: index + 1,
				interval: email.interval,
				catchup: email.catchup
			}));

			// Save both drip program and details
			forkJoin({
				program: this.dataService.saveDripProgram(this.dripProgram),
				details: this.dataService.saveDripProgramDetails(this.dripProgramID, details)
			}).subscribe({
				next: (res: any) => {
					this.siteService.setMessage('Drip program saved successfully');
					// Refresh the list to update dropdown labels
					this.dataService.getDripPrograms().subscribe(list => {
						this.dripPrograms = list;
					});
					this.loading = false;
					this.isDirty = false;
					this.syncProgramQueryParam(this.dripProgramID);
					this.missingEmployers = res?.details?.missingEmployers ?? [];
					if (this.missingEmployers.length) this.showMissingWarning = true;
					resolve(true);
				},
				error: (error) => {
					console.error('Error saving drip program:', error);
					this.siteService.setMessage('Error saving drip program', 'error');
					this.loading = false;
					reject(false);
				}
			});
		});
	}

	addProgram() {
		const now = new Date();
		const newProgramID = crypto.randomUUID();
		const newProgram: DripProgram = new DripProgram({
			dripProgramID: newProgramID,
			dripProgramDesc: 'New Program',
			startDate: now,
			deliveryTime: '09:00:00',
			paused: false,
			catchUpInterval: 0
		});
		
		// Add to local list and switch to it immediately without saving
		this.dripPrograms.push(newProgram);
		this.selectedDripProgramID = newProgramID;
		this.dripProgramID = newProgramID;
		this.dripProgram = newProgram;
		this.emails = [];
		
		// Set up delivery time for UI
		const [hours, minutes] = newProgram.deliveryTime.split(':');
		this.deliveryTimeDate = new Date();
		this.deliveryTimeDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
		
		this.updateHeaderButtons();
		this.markDirty();
	}

	deleteProgram() {
		const currentID = this.selectedDripProgramID || this.dripProgramID;
		if (!currentID) return;
		if (currentID === this.defaultDripProgramID) {
			this.siteService.setMessage('Cannot delete the default drip program', 'warn');
			return;
		}
		this.confirmationService.confirm({
			message: 'Delete this drip program? This cannot be undone.',
			header: 'Delete Drip Program',
			acceptLabel: 'Delete',
			acceptButtonStyleClass: 'danger',
			rejectLabel: 'Cancel',
			acceptIcon: 'trash',
			rejectIcon: 'times',
			accept: () => {
				this.loading = true;
				this.dataService.deleteDripProgram(currentID).subscribe({
					next: () => {
						forkJoin({
							list: this.dataService.getDripPrograms(),
							config: this.dataService.getDefaultDripProgramConfig()
						}).subscribe(({ list, config }) => {
							this.dripPrograms = list;
							this.defaultDripProgramID = config.defaultDripProgramID;
							// After delete, revert selection to default or first available
							const nextID = this.defaultDripProgramID || (list[0]?.dripProgramID ?? '');
							if (nextID) {
								this.selectProgram(nextID);
							} else {
								this.selectedDripProgramID = '';
								this.updateHeaderButtons();
								this.syncProgramQueryParam(null);
							}
						});
					},
					error: (error) => {
						console.error('Error deleting drip program:', error);
						// 400 = refused by the server (employers still on the program): show its message.
						this.siteService.setMessage(error?.status === 400 && error.error?.message ? error.error.message : 'Error deleting drip program', 'error', 6000);
						this.loading = false;
					}
				});
			}
		});
	}

	// --- Copy Program ---

	copyProgram() {
		if (!this.dripProgramID) return;
		if (this.isDirty) {
			this.confirmUnsavedChanges('Do you want to save before copying this program? The copy is made from the saved version.', () => this.openCopyDialog());
			return;
		}
		this.openCopyDialog();
	}

	private openCopyDialog() {
		const sourceDesc = this.findProgram(this.dripProgramID)?.dripProgramDesc || this.dripProgram.dripProgramDesc || 'Drip Program';
		this.copyProgramName = `${sourceDesc} - copy`;
		this.copyEmployerID = null;
		this.copyInFlight = false;
		this.showCopyDialog = true;
		if (!this.copyEmployers.length) {
			this.dataService.getEmployers().subscribe({
				next: (list) => {
					this.copyEmployers = list
						.map(e => new Employer(e))
						.sort((a, b) => (a.employerName ?? '').localeCompare(b.employerName ?? ''));
				},
				error: (error) => {
					console.error('Error loading employers:', error);
					this.siteService.setMessage('Error loading employers', 'error');
				}
			});
		}
	}

	closeCopyDialog() {
		if (this.copyInFlight) return;
		this.showCopyDialog = false;
	}

	get canCopy(): boolean {
		return !!this.copyProgramName?.trim() && !this.copyInFlight;
	}

	doCopyProgram() {
		if (!this.canCopy) return;
		const sourceID = this.dripProgramID;
		const newID = crypto.randomUUID();
		const employerName = this.copyEmployers.find(e => e.employerID === this.copyEmployerID)?.employerName;
		this.copyInFlight = true;
		this.dataService.copyDripProgram(sourceID, {
			newDripProgramID: newID,
			newDripProgramDesc: this.copyProgramName.trim(),
			employerID: this.copyEmployerID || null
		}).subscribe({
			next: (res) => {
				const createdID = res?.dripProgramID || newID;
				this.dataService.getDripPrograms().subscribe({
					next: (list) => {
						this.dripPrograms = list;
						this.copyInFlight = false;
						this.showCopyDialog = false;
						this.siteService.setMessage(employerName
							? `Drip program copied and assigned to ${employerName}`
							: 'Drip program copied');
						this.selectProgram(this.findProgram(createdID)?.dripProgramID ?? createdID);
					},
					error: (error) => {
						console.error('Error reloading drip programs after copy:', error);
						this.copyInFlight = false;
						this.showCopyDialog = false;
						this.siteService.setMessage('The program was copied, but the list could not be reloaded. Refresh the page.', 'warn', 7500);
					}
				});
			},
			error: (err) => {
				console.error('Error copying drip program:', err);
				this.copyInFlight = false;
				this.siteService.setMessage(this.serverMessage(err, 'Error copying drip program'), 'error', 7500);
			}
		});
	}

	private serverMessage(err: any, fallback: string): string {
		const e = err?.error;
		if (typeof e === 'string' && e.trim()) return e;
		if (e?.message) return e.message;
		if (e?.errors) {
			const first = Object.values(e.errors).flat()[0];
			if (typeof first === 'string') return first;
		}
		if (e?.title) return e.title;
		return fallback;
	}

	// --- Edit a step ---

	// The PendingChangesGuard on this route prompts to save/discard if the program has unsaved changes.
	editCampaign(email: DripProgramEmail) {
		if (!email?.campaignID || !this.dripProgramID) return;
		this.router.navigate(['/admin', 'campaigns', email.campaignID], { queryParams: { dripProgramID: this.dripProgramID } });
	}

	dropEmail(event: CdkDragDrop<DripProgramEmail[]>) {
		moveItemInArray(this.emails, event.previousIndex, event.currentIndex);
		// Update sequence numbers after reordering
		this.emails.forEach((email, index) => {
			email.sequence = index + 1;
		});
		this.markDirty();
	}

	showAddCampaignDialog() {
		// Get campaigns not already in the sequence
		const usedCampaignIDs = this.emails.map(e => e.campaignID);
		this.filteredAvailableCampaigns = this.availableCampaigns.filter(c => !usedCampaignIDs.includes(c.campaignID));
		
		if (this.filteredAvailableCampaigns.length === 0) {
			this.siteService.setMessage('No more campaigns available to add', 'warn');
			return;
		}

		this.campaignSearchTerm = "";
		this.showCampaignDialog = true;
	}

	filterCampaigns() {
		const usedCampaignIDs = this.emails.map(e => e.campaignID);
		this.filteredAvailableCampaigns = this.availableCampaigns
			.filter(c => !usedCampaignIDs.includes(c.campaignID))
			.filter(c => c.campaignName.toLowerCase().includes(this.campaignSearchTerm.toLowerCase()));
	}

	selectCampaignToAdd(campaign: Campaign) {
		this.addCampaign(campaign.campaignID, campaign.campaignName);
		this.closeCampaignDialog();
	}

	closeCampaignDialog() {
		this.showCampaignDialog = false;
		this.campaignSearchTerm = "";
	}

	addCampaign(campaignID: string, campaignName: string) {
		const newEmail: DripProgramEmail = {
			campaignID: campaignID,
			campaignName: campaignName,
			sequence: this.emails.length + 1,
			interval: 7, // Default to 7 days
			catchup: false
		};
		this.emails.push(newEmail);
		this.markDirty();
	}

	removeCampaign(index: number) {
		this.confirmationService.confirm({
			message: 'Are you sure you want to remove this campaign from the sequence?',
			header: 'Remove Campaign?',
			acceptLabel: 'Remove',
			acceptButtonStyleClass: 'danger',
			rejectLabel: 'Cancel',
			acceptIcon: 'check',
			rejectIcon: 'times',
			accept: () => {
				this.emails.splice(index, 1);
				// Update sequence numbers after removal
				this.emails.forEach((email, idx) => {
					email.sequence = idx + 1;
				});
				this.markDirty();
			}
		});
	}

	markDirty() {
		this.isDirty = true;
	}

	canDeactivate(): boolean | Observable<boolean> {
		return !this.isDirty;
	}

	// Unsaved-changes dialog: Save or Discard, then run the action the user asked for.
	private confirmUnsavedChanges(message: string, action: () => void) {
		this.dirtyDialogMessage = `You have unsaved changes. ${message}`;
		this.pendingAction = action;
		this.dirtySwitchDialogVisible = true;
	}

	onDirtySave() {
		const action = this.pendingAction;
		if (!action) { this.dirtySwitchDialogVisible = false; return; }
		this.save().then(() => {
			this.dirtySwitchDialogVisible = false;
			this.pendingAction = null;
			action();
		}).catch(() => { /* save() already showed the error; keep the dialog open */ });
	}

	onDirtyDiscard() {
		const action = this.pendingAction;
		this.dirtySwitchDialogVisible = false;
		this.pendingAction = null;
		if (!action) return;
		this.isDirty = false;
		action();
	}

	onDirtyCancel() {
		this.dirtySwitchDialogVisible = false;
		this.pendingAction = null;
		// A cancelled program switch leaves the dropdown on the new choice; put it back on the loaded program.
		this.selectedDripProgramID = this.dripProgramID;
	}

	// Closing with the X counts as Cancel (Save/Discard clear pendingAction before the dialog hides).
	onDirtyHide() {
		if (this.pendingAction) this.onDirtyCancel();
	}
}
